import { useEffect, useState } from 'preact/hooks';
import { useRelayStatus } from '../../utils/relayStatus';
import { relayKeyForRoom } from '../../utils/relayKey';
import { Wordmark } from '../components/Wordmark';

/**
 * Bridge setup page — opened on the LAPTOP/phone that will run the CAN bridge
 * (reached via the QR shown in the car).
 */
export function Bridge({ room }: { room: string }) {
  const origin = location.origin;
  const st = useRelayStatus(room);
  // the relay wants the room's key: the command line carries it, so the bridge needs no change of its own
  const [key, setKey] = useState('');
  useEffect(() => { void relayKeyForRoom(room).then(setKey).catch(() => setKey('')); }, [room]);
  const relay = `${origin.replace(/^http/, 'ws')}/ws/telemetry/${room}${key ? `?key=${key}` : ''}`;
  const cmd = `curl -O ${origin}/bridge/panda-bridge.mjs -O ${origin}/bridge/tesla-can.mjs && node panda-bridge.mjs --relay '${relay}'`;
  const live = st.pubs > 0 && st.hz > 0;
  return (
    <main class="bridge">
      <Wordmark class="bridge__wordmark" />
      <h1>CAN bridge setup · room <code>{room}</code></h1>
      <p class="bridge__lead">
        Live pedal / brake / rpm need an <b>Enhauto S3XY Commander (Gen2)</b>. The Tesla browser cannot talk to it
        directly, so a laptop (or Raspberry Pi) relays the data: <b>Commander → this bridge → roardrive.com → your car's screen</b>.
      </p>
      <ol class="bridge__steps">
        <li><b>S3XY app</b> → Commander → switch on <b>ScanMyTesla support</b> (and <b>ScanMyTesla Always ON</b>, so you don't need the app every trip). The Commander now broadcasts the Wi-Fi <code>S3XY_OBD</code> (password <code>12345678</code>); our bridge speaks the same <b>Panda Wi-Fi</b> protocol as ScanMyTesla / TesLAX.</li>
        <li>Connect this laptop to Wi-Fi <code>S3XY_OBD</code> <u>and</u> keep internet on a second path (USB/Bluetooth tethering from your phone, or a second Wi-Fi adapter).</li>
        <li>Install <a href="https://nodejs.org" target="_blank" rel="noreferrer">Node.js 22+</a> (once). Then run in a terminal:</li>
      </ol>
      <pre class="bridge__cmd" onClick={() => { try { void navigator.clipboard.writeText(cmd); } catch { /* ignore */ } }}>{cmd}</pre>
      <p class="bridge__hint">Tap the command to copy it. Keep the terminal open while you drive.</p>
      <ol class="bridge__steps" start={4}>
        <li>In the car: ROARDRIVE → Telemetry → <b>CAN</b> (same room <code>{room}</code>) → pick a car → START.</li>
      </ol>
      <div class="bridge__alt">
        <b>No laptop? Android phone instead</b> (iPhone can't run the bridge): install <a href="https://f-droid.org/packages/com.termux/" target="_blank" rel="noreferrer">Termux</a>,
        run <code>pkg install nodejs curl</code> once, join Wi-Fi <code>S3XY_OBD</code> (keep mobile data ON, Android uses it for internet while the
        Wi-Fi has none) and run the same command above. Keep Termux in the foreground or disable battery optimisation for it.
      </div>
      <div class={`bridge__status ${live ? 'bridge__status--ok' : st.pubs > 0 ? 'bridge__status--warn' : ''}`}>
        <i />
        {live
          ? `Bridge LIVE · ${st.hz} msg/s into room ${room}`
          : st.pubs > 0
            ? 'Bridge connected, waiting for CAN frames… (is the Commander on S3XY_OBD?)'
            : st.connected
              ? 'Waiting for the bridge to connect…'
              : 'Connecting to relay…'}
      </div>
      <p class="bridge__foot">
        Only one client can be on <code>S3XY_OBD</code> at a time; your S3XY buttons may drop while ScanMyTesla support is on
        (reboot the Commander to restore). <a href="/">← back to ROARDRIVE</a>
      </p>
    </main>
  );
}
